import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PlusIcon } from '@heroicons/react/24/outline';
import BannerCard from '../components/BannerCard';
import BannerModal from '../components/BannerModal';
import ConfirmDialog from '../components/ConfirmDialog';

const NRILoanBannersPage = () => {
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingBanner, setEditingBanner] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, bannerId: null });

    // Fetch banners (filtered for nri_loan page type)
    const fetchBanners = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/admin/banners');
            // Filter for nri_loan page type only
            const filteredBanners = (response.data.data || []).filter(
                banner => banner.page_type === 'nri_loan'
            );
            setBanners(filteredBanners);
        } catch (error) {
            console.error('Error fetching banners:', error);
            toast.error('Failed to load banners');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBanners();
    }, []);

    // Handle add new banner
    const handleAddNew = () => {
        setEditingBanner(null);
        setIsModalOpen(true);
    };

    // Handle edit banner
    const handleEdit = (banner) => {
        setEditingBanner(banner);
        setIsModalOpen(true);
    };

    // Handle delete banner
    const handleDelete = (bannerId) => {
        setDeleteConfirm({ isOpen: true, bannerId });
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`/api/admin/banners/${deleteConfirm.bannerId}`);
            toast.success('Banner deleted successfully');
            fetchBanners();
        } catch (error) {
            console.error('Error deleting banner:', error);
            toast.error('Failed to delete banner');
        }
    };

    // Handle toggle active status
    const handleToggleActive = async (banner) => {
        try {
            await axios.put(`/api/admin/banners/${banner.id}`, {
                ...banner,
                is_active: !banner.is_active,
                highlights: typeof banner.highlights === 'string'
                    ? banner.highlights
                    : JSON.stringify(banner.highlights)
            });
            toast.success(`Banner ${!banner.is_active ? 'activated' : 'deactivated'} successfully`);
            fetchBanners();
        } catch (error) {
            console.error('Error toggling banner status:', error);
            toast.error('Failed to update banner status');
        }
    };

    // Handle modal close
    const handleModalClose = () => {
        setIsModalOpen(false);
        setEditingBanner(null);
    };

    // Handle save (create or update)
    const handleSave = () => {
        handleModalClose();
        fetchBanners();
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">NRI Loan - Banner Management</h1>
                    <p className="text-slate-500 mt-1">Manage banners for NRI Loan page</p>
                </div>
                <button
                    onClick={handleAddNew}
                    className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                    <PlusIcon className="h-5 w-5" />
                    Add New Banner
                </button>
            </div>

            {/* Loading State */}
            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="bg-white rounded-2xl p-6 shadow-sm animate-pulse">
                            <div className="aspect-video bg-slate-200 rounded-xl mb-4"></div>
                            <div className="h-6 bg-slate-200 rounded mb-2"></div>
                            <div className="h-4 bg-slate-200 rounded w-3/4"></div>
                        </div>
                    ))}
                </div>
            ) : banners.length === 0 ? (
                /* Empty State */
                <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
                    <div className="max-w-md mx-auto">
                        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <PlusIcon className="h-8 w-8 text-slate-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-slate-800 mb-2">No banners yet</h3>
                        <p className="text-slate-500 mb-6">
                            Get started by creating your first NRI Loan banner
                        </p>
                        <button
                            onClick={handleAddNew}
                            className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200"
                        >
                            Create First Banner
                        </button>
                    </div>
                </div>
            ) : (
                /* Banners Grid */
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {banners.map((banner) => (
                        <BannerCard
                            key={banner.id}
                            banner={banner}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                        />
                    ))}
                </div>
            )}

            {/* Banner Modal */}
            {isModalOpen && (
                <BannerModal
                    banner={editingBanner}
                    onClose={handleModalClose}
                    onSave={handleSave}
                    defaultPageType="nri_loan"
                />
            )}

            {/* Delete Confirm Dialog */}
            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, bannerId: null })}
                onConfirm={confirmDelete}
                title="Delete Banner"
                message="Are you sure you want to delete this banner? This action cannot be undone."
            />
        </div>
    );
};

export default NRILoanBannersPage;
